'use client';
import React from 'react';
import { Button } from '@/core/ui/primitives/button';
import type { MindmapLayoutDirection, MindmapNodeShape } from '@/features/mindmap/core/MindmapTypes';
type StylePatch = Partial<Pick<ToolbarSelectedSnapshot, 'textColor' | 'bgColor' | 'borderColor' | 'shape'>>;
interface ToolbarSelectedSnapshot {
  textColor: string;
  bgColor: string;
  borderColor: string;
  shape: MindmapNodeShape;
}
export interface NodeStyleToolbarProps {
  isRoot?: boolean;
  layoutDirection?: MindmapLayoutDirection;
  selected?: Partial<ToolbarSelectedSnapshot>;
  onPreview?: (style: StylePatch) => void;
  onCommit?: (style: StylePatch) => void;
  onChangeDirection?: (direction: MindmapLayoutDirection) => void;
  onPreviewDirection?: (direction: MindmapLayoutDirection) => void;
  onResetDirectionPreview?: () => void;
  disabled?: boolean;
  selectedId?: string;
  isDragging?: boolean;
}
export const NODE_STYLE_COLOR_SWATCHES = [
  '#ffffff',
  '#6b7280',
  '#111827',
  '#ef4444',
  '#f59e0b',
  '#10b981',
  '#3b82f6',
  '#8b5cf6',
];
export function NodeStyleToolbar({
  isRoot,
  layoutDirection = 'right',
  selected,
  onPreview,
  onCommit,
  onChangeDirection,
  onPreviewDirection,
  onResetDirectionPreview,
  disabled,
  selectedId,
  isDragging,
}: NodeStyleToolbarProps) {
  const swatches = NODE_STYLE_COLOR_SWATCHES;
  const origRef = React.useRef<ToolbarSelectedSnapshot | null>(null);
  const [openPanel, setOpenPanel] = React.useState<null | 'text' | 'bg' | 'border' | 'shape' | 'direction'>(null);
  const directionPreviewTimeoutRef = React.useRef<number | null>(null);
  const panelHideTimeoutRef = React.useRef<number | null>(null);
  const ensureSnapshot = () => {
    if (!origRef.current) {
      origRef.current = {
        textColor: selected?.textColor ?? '#111827',
        bgColor: selected?.bgColor ?? '#ffffff',
        borderColor: selected?.borderColor ?? '#9ca3af',
        shape: selected?.shape ?? 'rounded',
      };
    }
  };
  const current = React.useMemo(() => {
    const base = origRef.current;
    return {
      text: base?.textColor ?? selected?.textColor ?? '#111827',
      bg: base?.bgColor ?? selected?.bgColor ?? '#ffffff',
      border: base?.borderColor ?? selected?.borderColor ?? '#9ca3af',
      shape: base?.shape ?? selected?.shape ?? 'rounded',
    };
  }, [selected]);
  React.useEffect(() => {
    origRef.current = null;
  }, [selectedId]);
  React.useEffect(() => {
    if (isDragging && openPanel) {
      setOpenPanel(null);
    }
  }, [isDragging, openPanel]);
  React.useEffect(() => {
    return () => {
      if (directionPreviewTimeoutRef.current != null) {
        window.clearTimeout(directionPreviewTimeoutRef.current);
        directionPreviewTimeoutRef.current = null;
      }
      if (panelHideTimeoutRef.current != null) {
        window.clearTimeout(panelHideTimeoutRef.current);
        panelHideTimeoutRef.current = null;
      }
    };
  }, []);
  const schedulePanelHide = (resetCallback: () => void) => {
    if (panelHideTimeoutRef.current != null) {
      window.clearTimeout(panelHideTimeoutRef.current);
    }
    panelHideTimeoutRef.current = window.setTimeout(() => {
      resetCallback();
      setOpenPanel(null);
      panelHideTimeoutRef.current = null;
    }, 1000);
  };
  const clearPanelHideTimeout = () => {
    if (panelHideTimeoutRef.current != null) {
      window.clearTimeout(panelHideTimeoutRef.current);
      panelHideTimeoutRef.current = null;
    }
  };
  const queueDirectionReset = () => {
    if (directionPreviewTimeoutRef.current != null) {
      window.clearTimeout(directionPreviewTimeoutRef.current);
      directionPreviewTimeoutRef.current = null;
    }
    directionPreviewTimeoutRef.current = window.setTimeout(() => {
      directionPreviewTimeoutRef.current = null;
      onResetDirectionPreview?.();
    }, 120);
  };
  const triggerDirectionPreview = (dir: MindmapLayoutDirection) => {
    if (directionPreviewTimeoutRef.current != null) {
      window.clearTimeout(directionPreviewTimeoutRef.current);
      directionPreviewTimeoutRef.current = null;
    }
    onPreviewDirection?.(dir);
  };
  const directionOptions = React.useMemo(
    () => [
      { key: 'left' as MindmapLayoutDirection, icon: '←', label: 'Layout left' },
      { key: 'right' as MindmapLayoutDirection, icon: '→', label: 'Layout right' },
      { key: 'up' as MindmapLayoutDirection, icon: '↑', label: 'Layout up' },
      { key: 'down' as MindmapLayoutDirection, icon: '↓', label: 'Layout down' },
    ],
    []
  );
  const currentDirection = React.useMemo(
    () => directionOptions.find((opt) => opt.key === layoutDirection) ?? directionOptions[1],
    [directionOptions, layoutDirection]
  );
  const handlePreview = (patch: StylePatch) => {
    onPreview?.(patch);
  };
  const handleCommit = (patch: StylePatch) => {
    if (origRef.current) {
      if (patch.textColor) origRef.current.textColor = patch.textColor;
      if (patch.bgColor) origRef.current.bgColor = patch.bgColor;
      if (patch.borderColor) origRef.current.borderColor = patch.borderColor;
      if (patch.shape) origRef.current.shape = patch.shape;
    }
    onCommit?.(patch);
    setOpenPanel(null);
  };
  const handleReset = () => {
    const snapshot = origRef.current;
    if (!snapshot) return;
    onPreview?.({
      textColor: snapshot.textColor,
      bgColor: snapshot.bgColor,
      borderColor: snapshot.borderColor,
      shape: snapshot.shape,
    });
  };
  return (
    <div className='z-30 flex items-center gap-0.5 rounded-md border border-gray-300 bg-white/95 px-1 py-0.5 text-[10px] text-gray-700 shadow backdrop-blur dark:border-gray-700 dark:bg-gray-900/90 dark:text-gray-200'>
      <div className='group relative inline-flex items-center'>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          aria-label='Text color'
          className='h-5 px-1 text-[11px] font-semibold'
          style={{ color: current.text }}
          disabled={disabled}
          onMouseEnter={() => {
            if (disabled || isDragging) return;
            ensureSnapshot();
            setOpenPanel('text');
          }}>
          Aa
        </Button>
        <div className='absolute left-0 top-[-8px] h-2 w-6'></div>
        <div
          className={`pointer-events-auto absolute left-0 bottom-full mb-1 z-40 ${
            openPanel === 'text' && !disabled ? 'flex' : 'hidden'
          } flex-col gap-0.5 rounded-md border border-gray-300 bg-white p-1 shadow dark:border-gray-700 dark:bg-gray-900`}
          onMouseEnter={() => {
            if (disabled || isDragging) return;
            clearPanelHideTimeout();
            ensureSnapshot();
            setOpenPanel('text');
          }}
          onMouseLeave={() => {
            schedulePanelHide(() => handlePreview({ textColor: origRef.current?.textColor ?? current.text }));
          }}>
          {swatches.map((c) => (
            <Button
              key={`t-${c}`}
              type='button'
              variant='ghost'
              size='sm'
              aria-label={`Text ${c}`}
              className='h-3.5 w-3.5 text-[11px] font-semibold bg-transparent border-0 rounded-none shadow-none'
              style={{ color: c }}
              onMouseEnter={() => handlePreview({ textColor: c })}
              onClick={() => handleCommit({ textColor: c })}>
              Aa
            </Button>
          ))}
        </div>
      </div>

      <div className='group relative inline-flex items-center'>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          aria-label='Background color'
          className='h-5 w-5 rounded-full border'
          style={{ backgroundColor: current.bg, borderColor: '#9ca3af' }}
          disabled={disabled}
          onMouseEnter={() => {
            if (disabled || isDragging) return;
            ensureSnapshot();
            setOpenPanel('bg');
          }}
        />
        <div className='absolute left-0 top-[-8px] h-2 w-6'></div>
        <div
          className={`pointer-events-auto absolute left-0 bottom-full mb-1 z-40 ${
            openPanel === 'bg' && !disabled ? 'flex' : 'hidden'
          } flex-col gap-0.5 rounded-md border border-gray-300 bg-white p-1 shadow dark:border-gray-700 dark:bg-gray-900`}
          onMouseEnter={() => {
            if (disabled || isDragging) return;
            clearPanelHideTimeout();
            ensureSnapshot();
            setOpenPanel('bg');
          }}
          onMouseLeave={() => {
            schedulePanelHide(() => handlePreview({ bgColor: origRef.current?.bgColor ?? current.bg }));
          }}>
          {swatches.map((c) => (
            <Button
              key={`bg-${c}`}
              type='button'
              variant='ghost'
              size='sm'
              aria-label={`BG ${c}`}
              className='h-3.5 w-3.5 rounded-full border border-gray-300 dark:border-gray-700'
              style={{ backgroundColor: c }}
              onMouseEnter={() => handlePreview({ bgColor: c })}
              onClick={() => handleCommit({ bgColor: c })}
            />
          ))}
        </div>
      </div>

      <div className='group relative inline-flex items-center'>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          aria-label='Border color'
          className='h-5 w-5 rounded-full border'
          style={{ borderColor: current.border, backgroundColor: 'transparent' }}
          disabled={disabled}
          onMouseEnter={() => {
            if (disabled || isDragging) return;
            ensureSnapshot();
            setOpenPanel('border');
          }}
        />
        <div className='absolute left-0 top-[-8px] h-2 w-6'></div>
        <div
          className={`pointer-events-auto absolute left-0 bottom-full mb-1 z-40 ${
            openPanel === 'border' && !disabled ? 'flex' : 'hidden'
          } flex-col gap-0.5 rounded-md border border-gray-300 bg-white p-1 shadow dark:border-gray-700 dark:bg-gray-900`}
          onMouseEnter={() => {
            if (disabled || isDragging) return;
            clearPanelHideTimeout();
            ensureSnapshot();
            setOpenPanel('border');
          }}
          onMouseLeave={() => {
            schedulePanelHide(() => handlePreview({ borderColor: origRef.current?.borderColor ?? current.border }));
          }}>
          {swatches.map((c) => (
            <Button
              key={`bd-${c}`}
              type='button'
              variant='ghost'
              size='sm'
              aria-label={`Border ${c}`}
              className='h-3.5 w-3.5 rounded-full border'
              style={{ borderColor: c }}
              onMouseEnter={() => handlePreview({ borderColor: c })}
              onClick={() => handleCommit({ borderColor: c })}
            />
          ))}
        </div>
      </div>

      <div className='group relative inline-flex items-center'>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          aria-label='Shape'
          className='h-5 w-5 rounded border border-gray-300 text-[11px] leading-4 dark:border-gray-700'
          disabled={disabled}
          onMouseEnter={() => {
            if (disabled || isDragging) return;
            ensureSnapshot();
            setOpenPanel('shape');
          }}>
          {current.shape === 'rounded'
            ? '◼︎'
            : current.shape === 'rectangle'
            ? '▭'
            : current.shape === 'pill'
            ? '⬭'
            : '▁'}
        </Button>
        <div className='absolute left-0 top-[-8px] h-2 w-6'></div>
        <div
          className={`pointer-events-auto absolute left-0 bottom-full mb-1 z-40 ${
            openPanel === 'shape' && !disabled ? 'flex' : 'hidden'
          } flex-col gap-0.5 rounded-md border border-gray-300 bg-white p-1 shadow dark:border-gray-700 dark:bg-gray-900`}
          onMouseEnter={() => {
            if (disabled || isDragging) return;
            clearPanelHideTimeout();
            ensureSnapshot();
            setOpenPanel('shape');
          }}
          onMouseLeave={() => {
            schedulePanelHide(() => handlePreview({ shape: origRef.current?.shape ?? current.shape }));
          }}>
          {(
            [
              { id: 'rounded', label: '◼︎' },
              { id: 'rectangle', label: '▭' },
              { id: 'pill', label: '⬭' },
              { id: 'underline', label: '▁' },
            ] as Array<{
              id: MindmapNodeShape;
              label: string;
            }>
          ).map((s) => (
            <Button
              key={s.id}
              type='button'
              variant='ghost'
              size='sm'
              className='h-4 w-4 rounded border border-gray-300 text-[11px] leading-4 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'
              onMouseEnter={() => handlePreview({ shape: s.id })}
              onClick={() => handleCommit({ shape: s.id })}>
              {s.label}
            </Button>
          ))}
        </div>
      </div>

      {isRoot && (
        <div className='group relative inline-flex items-center'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            aria-label={currentDirection.label}
            className='ml-1 h-5 w-5 rounded border border-gray-300 text-[10px] leading-4 dark:border-gray-700'
            disabled={disabled}
            onMouseEnter={() => {
              if (disabled || isDragging) return;
              setOpenPanel('direction');
            }}>
            {currentDirection.icon}
          </Button>
          <div className='absolute left-0 top-[-8px] h-2 w-6'></div>
          <div
            className={`pointer-events-auto absolute left-0 bottom-full mb-1 z-40 ${
              openPanel === 'direction' && !disabled ? 'flex' : 'hidden'
            } flex-col gap-0.5 rounded-md border border-gray-300 bg-white p-1 shadow dark:border-gray-700 dark:bg-gray-900`}
            onMouseEnter={() => {
              if (disabled || isDragging) return;
              clearPanelHideTimeout();
              setOpenPanel('direction');
            }}
            onMouseLeave={() => {
              if (disabled || isDragging) return;
              schedulePanelHide(() => queueDirectionReset());
            }}>
            {directionOptions.map(({ key, icon, label }) => (
              <Button
                key={key}
                type='button'
                variant='ghost'
                size='sm'
                aria-label={label}
                disabled={disabled}
                className={`h-4 w-4 rounded border text-[10px] leading-4 transition-colors ${
                  layoutDirection === key
                    ? 'border-gray-400 bg-gray-200 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300'
                } ${disabled ? 'opacity-60' : ''}`}
                onMouseEnter={() => {
                  if (disabled || isDragging) return;
                  triggerDirectionPreview(key);
                }}
                onClick={() => {
                  if (disabled || isDragging) return;
                  if (directionPreviewTimeoutRef.current != null) {
                    window.clearTimeout(directionPreviewTimeoutRef.current);
                    directionPreviewTimeoutRef.current = null;
                  }
                  setOpenPanel(null);
                  onChangeDirection?.(key);
                }}>
                {icon}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
